import { type StockStatus } from '@choco-admin/types';

import { isIntegrationControlledField } from '../../../featureFlags/integrationSettings';
import type { SupplierUpdateInput } from '../../../generated';
// The module rather than the barrel: the barrel also exports the hook, which
// pulls Amplify into every test that touches this pure builder.
import type { SupplierFormData } from '../components/SupplierForm/types';

import { withOutOfStockVisibility } from './buyerVisibleStockStatuses';

/**
 * Builds the `supplierUpdate` payload for the distributor edit form.
 *
 * The form holds every field across six tabs, and it used to submit all of them
 * on every save. Nudging one polygon corner or changing the language therefore
 * rewrote the description, the delivery fee and the catalog setting too, using
 * whatever the form happened to be holding. This sends only what changed.
 *
 * Omitting a key is safe. `supplierUpdate` merges the payload over the stored
 * row (`buildSupplierTableUpdate`) and the DynamoDB write drops undefined
 * values, so an absent key leaves the stored value alone. Verified live on
 * chocoautumn 2026-07-30: a payload of `{id, deliveryZones}` left
 * `minOrderAmount`, `integration`, `hasEnabledFractionalUnits`, `description`
 * and `deliveryCosts` untouched.
 *
 * Two consequences of that same merge are worth knowing:
 *
 * - **`null` cannot clear a field.** The backend runs `defaultNullToUndefined`
 *   over the payload, so a null is dropped exactly like an absent key. Clearing
 *   a text field means sending an empty string.
 * - **Some keys must be sent even when unchanged.** See `isTest` and
 *   `integration` below, both of which feed the distributor's bot user.
 */

/**
 * Whether a field was edited. Structurally compatible with React Hook Form's
 * `dirtyFields`, and takeable as a plain object in tests.
 */
export type DirtyFormFields = Partial<Record<keyof SupplierFormData, unknown>>;

/**
 * The values the form loaded, as React Hook Form holds them.
 *
 * Structural rather than `Partial<SupplierFormData>`, because RHF's
 * `defaultValues` is a readonly deep partial: every leaf is optional and arrays
 * come back holding `undefined`. Only presence and the raw value are needed here.
 */
export type LoadedFormValues = Readonly<
  Partial<Record<keyof SupplierFormData, unknown>>
>;

/**
 * Whether a locked save refuses `field`.
 *
 * `isIntegrated` is refused alongside the four settings even though it is not one
 * of them. Turning it off unlocks all four, since they are only locked while it
 * is on, so a save carrying it would walk straight through the guard.
 */
const isIntegrationProtectedField = (field: keyof SupplierFormData): boolean =>
  isIntegrationControlledField(field) || field === 'isIntegrated';

/**
 * The edited fields a locked save will refuse.
 *
 * The caller needs this to tell the truth afterwards. A refused field is absent
 * from the payload, so the save succeeds on everything else and the form would
 * otherwise mark the refused edit clean, leaving a value on screen that is not
 * stored anywhere and disappears on the next load.
 */
export const refusedIntegrationFields = (
  dirtyFields: DirtyFormFields,
  integrationSettingsLocked: boolean
): (keyof SupplierFormData)[] => {
  if (!integrationSettingsLocked) return [];

  return (Object.keys(dirtyFields) as (keyof SupplierFormData)[]).filter(
    (field) => dirtyFields[field] && isIntegrationProtectedField(field)
  );
};

/**
 * What to rebaseline the form to once a save has returned.
 *
 * The submitted values, except that anything the backend did not store goes back
 * to what the form loaded. Two things reach it that way: an edit the integration
 * guard refused, and a delivery field whose own mutation failed.
 *
 * Both need the same treatment for the same reason, which is `reset`. It makes
 * whatever it is given the new default, so a field baselined to the submitted
 * value cannot be marked dirty afterwards by setting it to that same value,
 * however `shouldDirty` is passed. Baseline it to the loaded value instead and
 * the caller decides what happens next:
 *
 * - A refused edit is left alone, so the screen matches storage. The person
 *   cannot save it anyway, and a dirty field they cannot save leaves the form
 *   permanently unsaveable.
 * - A failed delivery edit is written back by the caller with `shouldDirty`, so
 *   it is both on screen and retryable, which is what its error message promises.
 */
export const buildPostSaveBaseline = (
  submitted: SupplierFormData,
  loaded: LoadedFormValues | undefined,
  notStored: readonly (keyof SupplierFormData)[]
): SupplierFormData => {
  const baseline = { ...submitted };

  notStored.forEach((field) => {
    if (loaded && field in loaded) {
      Object.assign(baseline, { [field]: loaded[field] });
    }
  });

  return baseline;
};

/** Form field to input field, for every plain scalar the form owns. */
const SCALAR_FIELDS: ReadonlyArray<{
  input: keyof SupplierUpdateInput;
  form: keyof SupplierFormData;
}> = [
  { input: 'name', form: 'name' },
  { input: 'description', form: 'description' },
  { input: 'primaryAssortment', form: 'primaryCategory' },
  { input: 'secondaryAssortments', form: 'secondaryCategories' },
  { input: 'deliveryCosts', form: 'deliveryFee' },
  { input: 'allowCustomProducts', form: 'allowCustomProducts' },
  { input: 'customerNumberMandatory', form: 'mandatoryCustomerNumber' },
  { input: 'deliveryDateMandatory', form: 'mandatoryDeliveryDate' },
  { input: 'allowBuyerComment', form: 'enableProductComments' },
  { input: 'enableFractionalUnits', form: 'allowFractionalUnits' },
  { input: 'allowOnlyOneActiveVariant', form: 'allowOnlyOneVariant' },
  { input: 'hideMyPrice', form: 'supplierHideMyPrice' },
  { input: 'isIntegrated', form: 'isIntegrated' },
  { input: 'enablePromotions', form: 'enablePromotions' },
  { input: 'enableTaxes', form: 'enableTaxes' },
  { input: 'pricingDateSource', form: 'pricingDateSource' },
  { input: 'calculationPrecision', form: 'calculationPrecision' },
  { input: 'locale', form: 'locale' },
  { input: 'timezone', form: 'timezone' },
  {
    input: 'allowInvoiceOrderGuidePhotoUpload',
    form: 'allowInvoiceOrderGuides',
  },
  { input: 'disableCustomerNumberEdits', form: 'disableCustomerNumberEdits' },
  // No inversion. The stored field means "merging is off", so the form's value
  // passes through unchanged and only the names disagree.
  {
    input: 'allowOrderedProductMerging',
    form: 'disableOrderedProductMerging',
  },
  {
    input: 'enableEcommerceProductGridView',
    form: 'enableEcommerceProductGrid',
  },
  { input: 'allowEcommercePoNumber', form: 'allowEcommercePoNumber' },
  { input: 'allowCustomerAcquisition', form: 'allowCustomerAcquisition' },
];

/** Fields that only ever travel together, keyed by what marks them dirty. */
const PRICE_SETTING_FIELDS: ReadonlyArray<keyof SupplierFormData> = [
  'displayInMarketplace',
  'displayToCustomers',
];

const MIN_ORDER_FIELDS: ReadonlyArray<keyof SupplierFormData> = [
  'minimumOrderValue',
  'minimumOrderCurrency',
  'minOrderStrict',
];

export interface BuildSupplierUpdateParams {
  data: SupplierFormData;
  dirtyFields: DirtyFormFields;
  /**
   * The loaded distributor's `isTest`. Always echoed back, because
   * `buildBotUserUpdateInput` sets the bot user's `isTest` and `chocoUser` from
   * `!!supplier.isTest` on every update. Omitting the key clears both flags on
   * the bot user, which is not something a profile edit should do.
   */
  loadedIsTest: boolean | null | undefined;
  /**
   * The loaded distributor's `buyerVisibleStockStatuses`. The checkbox only
   * toggles out-of-stock, so any other stored status is sent back unchanged.
   */
  loadedBuyerVisibleStockStatuses?: readonly StockStatus[] | null;
  /**
   * The full integration array, already merged over the distributor's existing
   * one by `buildIntegrationPayload`.
   *
   * Included whenever the distributor has integration values at all, not only
   * when a notification field changed. The backend derives the bot user's email
   * from `getSupplierEmail(supplier.integration)` on every update, so omitting
   * the key risks clearing that email. Sending the unchanged array is a no-op
   * for the distributor and keeps the bot user intact.
   */
  integrationPayload?: SupplierUpdateInput['integration'];
  /**
   * The min-order payload, or undefined when the form has no value. Sent only
   * when a min-order field changed. `minOrderAmount` is recomputed server-side
   * from this object, so price and strict always travel together.
   */
  minOrderPayload?: SupplierUpdateInput['minOrder'];
  /** The canonical `defaultSubCatalog` / `noDefaultCatalog` pair. */
  catalogPayload: {
    defaultSubCatalog: string;
    noDefaultCatalog: boolean;
  };
  /**
   * Whether the settings an integration depends on are read-only for the person
   * editing (an integrated distributor plus no
   * `adminCanEditSupplierIntegrationSettings`).
   *
   * This is the part that actually refuses the write. A disabled control can be
   * bypassed: a value seeded before the flags resolved, or a field dirtied from
   * another tab, and every one of those still reaches this builder. An omitted
   * key is a merge that keeps the stored value, so dropping the field is how the
   * refusal is expressed.
   */
  integrationSettingsLocked?: boolean;
}

const isDirty = (
  dirtyFields: DirtyFormFields,
  fields: ReadonlyArray<keyof SupplierFormData>
): boolean => fields.some((field) => Boolean(dirtyFields[field]));

export const buildSupplierUpdateInput = ({
  data,
  dirtyFields,
  loadedIsTest,
  loadedBuyerVisibleStockStatuses,
  integrationPayload,
  minOrderPayload,
  catalogPayload,
  integrationSettingsLocked = false,
}: BuildSupplierUpdateParams): Omit<SupplierUpdateInput, 'id'> => {
  const input: Omit<SupplierUpdateInput, 'id'> = {
    isTest: Boolean(loadedIsTest),
  };

  /** Whether `field` may be sent at all, before asking whether it changed. */
  const isSendable = (field: keyof SupplierFormData): boolean =>
    !integrationSettingsLocked || !isIntegrationProtectedField(field);

  SCALAR_FIELDS.forEach(({ input: inputKey, form }) => {
    if (!dirtyFields[form]) return;
    if (!isSendable(form)) return;
    // The pairs above are hand-checked, so the value at `form` is the type the
    // input expects at `inputKey`.
    Object.assign(input, { [inputKey]: data[form] });
  });

  // sortCartBy has no matching form field name and needs normalising.
  if (dirtyFields.sortBy) {
    input.sortCartBy =
      data.sortBy === 'alphabetical' ? 'alphabetical' : 'orderGuide';
  }

  // `priceSetting` replaces the stored object, so its two flags always travel
  // together and the locked one cannot simply be left out of the object. What is
  // dropped instead is the reason to send it: a locked price-visibility edit no
  // longer produces a payload, while an edit to the flag beside it still does
  // and carries the stored visibility, which the disabled control never changed.
  if (isDirty(dirtyFields, PRICE_SETTING_FIELDS.filter(isSendable))) {
    input.priceSetting = {
      displayInMarketplace: data.displayInMarketplace,
      displayToCustomers: data.displayToCustomers,
    };
  }

  if (isDirty(dirtyFields, MIN_ORDER_FIELDS) && minOrderPayload !== undefined) {
    input.minOrder = minOrderPayload;
  }

  if (dirtyFields.showOutOfStockProducts) {
    input.buyerVisibleStockStatuses = withOutOfStockVisibility(
      loadedBuyerVisibleStockStatuses,
      data.showOutOfStockProducts
    );
  }

  if (dirtyFields.supplierDefaultSubCatalog) {
    input.defaultSubCatalog = catalogPayload.defaultSubCatalog;
    input.noDefaultCatalog = catalogPayload.noDefaultCatalog;
  }

  if (integrationPayload !== undefined) {
    input.integration = integrationPayload;
  }

  return input;
};

/**
 * The form fields that feed `integration`. Editing one of these changes the
 * payload's `integration` array and nothing else.
 */
export const NOTIFICATION_FIELDS: ReadonlyArray<keyof SupplierFormData> = [
  'notificationOrderEmails',
  'notificationSystemAlertEmails',
  'notificationComplaintEmails',
  'notificationWhatsappNumber',
  'notificationSmsNumber',
  // The attachment flags live on the email entry, so editing one produces an
  // `integration` payload and nothing else. Without them here a checkbox-only
  // edit looks like no change at all and the mutation is skipped.
  'notificationOrderPdf',
  'notificationOrderXlsx',
];

/**
 * The subset of notification fields that feed the email integration entry.
 *
 * Kept apart from the phone channels because only email has a preserve branch:
 * an empty WhatsApp or SMS field simply produces no entry, while an empty email
 * field is ambiguous between "never set" and "just deleted".
 */
export const EMAIL_NOTIFICATION_FIELDS: ReadonlyArray<keyof SupplierFormData> =
  [
    'notificationOrderEmails',
    'notificationSystemAlertEmails',
    'notificationComplaintEmails',
    // Also email-entry fields: an edit to either has to stop the preserve
    // branch writing the stored flags back over the checkbox.
    'notificationOrderPdf',
    'notificationOrderXlsx',
  ];

/**
 * Whether the person editing actually touched the email destinations.
 *
 * The integration payload preserves an existing email entry when the form's
 * email fields are all empty, so that saving from another tab does not wipe it.
 * That guard cannot tell an untouched field from one just cleared, so clearing
 * the last order email restored it and still reported success. This is what
 * separates the two cases.
 */
export const hasEditedEmailNotifications = (
  dirtyFields: DirtyFormFields
): boolean => isDirty(dirtyFields, EMAIL_NOTIFICATION_FIELDS);

/**
 * Whether the person editing touched any notification destination at all.
 *
 * Clearing the last one leaves nothing to send, and an omitted `integration`
 * key is a merge that keeps what is stored. This is what tells "there was never
 * anything here" from "it was just emptied", so the second case can send an
 * empty array and actually clear it.
 */
export const hasEditedNotifications = (dirtyFields: DirtyFormFields): boolean =>
  isDirty(dirtyFields, NOTIFICATION_FIELDS);

/**
 * Whether this payload is worth sending.
 *
 * `isTest` and `integration` are always present, so their presence proves
 * nothing. But `integration` is also the ONLY key a notification edit produces,
 * so a naive "ignore both" check silently drops exactly those edits: changing a
 * delivery email and saving would report success and write nothing. The dirty
 * fields are therefore consulted, not just the payload shape.
 */
export const hasSupplierChanges = (
  input: Omit<SupplierUpdateInput, 'id'>,
  dirtyFields: DirtyFormFields = {}
): boolean => {
  const hasRealField = Object.keys(input).some(
    (key) => key !== 'isTest' && key !== 'integration'
  );

  return hasRealField || isDirty(dirtyFields, NOTIFICATION_FIELDS);
};
